'use client';
import { LogOut, ShieldX, ChevronRight } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, useSidebar } from '@/components/ui/sidebar';
import { UserRoleEnum } from 'src/graphql/schema';
import { useAuth } from 'src/context/auth/use-auth';
import useImpersonate from 'src/hooks/use-impersonate';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { getInitials } from 'src/widgets/AppCard/model';

type Props = {
  user: {
    name: string;
    email: string;
    role: UserRoleEnum;
  };
  companyName: string;
};

export function NavUser({ user, companyName }: Props) {
  const { isMobile } = useSidebar();
  const { signOut } = useAuth();
  const { isImpersonating, stopImpersonating } = useImpersonate();

  const fallback = getInitials(user.name);
  const avatarSrc = getGravatarUrl(user.email);

  return (
    <SidebarMenu className="gap-0">
      <SidebarSeparator className="mx-0" />
      <SidebarMenuItem className="group-data-[collapsible=icon]:py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground rounded-none group-data-[collapsible=icon]:size-9! group-data-[collapsible=icon]:rounded-md"
            >
              <Avatar className="size-9 rounded-md">
                <AvatarImage src={avatarSrc} />
                <AvatarFallback className="rounded-md bg-slate-400 text-sm font-semibold text-white">
                  {fallback}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-bold">{user.name}</span>
                <span className="truncate text-xs">
                  <span className="font-bold text-accent-software-escrow uppercase tracking-[0.03em]">{user.role}</span>{' '}
                  <span className="text-muted-foreground">· {companyName}</span>
                </span>
              </div>
              <ChevronRight className="ml-auto size-4!" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="size-8 rounded-md">
                  <AvatarImage src={avatarSrc} />
                  <AvatarFallback className="rounded-md bg-slate-400 text-sm font-semibold text-white">
                    {fallback}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isImpersonating && (
              <DropdownMenuItem onClick={stopImpersonating}>
                <ShieldX />
                Stop impersonating
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={signOut}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
